import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
    removeUsers as remove,
    undoUsers as undo,
    clearUsers as clear,
    updateUser as update,
} from "../actions";
import { fetchSchoolDetails } from "../../Schools/actions";
import { userLogin } from "../../../actions";
import api, { setToken } from "../../../../api/api";
import history from "../../../../history";

/**
 * Custom hook encapsulating all administrative user action handlers.
 * Extracted from index.js to keep the main component lean.
 */
const useUserActions = ({ filters, handleSearchSync }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const [activeModal, setActiveModal] = useState(null);
    const [activeSubModal, setActiveSubModal] = useState(null);
    const [activeUser, setActiveUser] = useState(null);

    const [selectedSchoolId, setSelectedSchoolId] = useState(null);
    const [schoolModalOpen, setSchoolModalOpen] = useState(false);
    const [schoolUsers, setSchoolUsers] = useState([]);
    const [loadingSchoolUsers, setLoadingSchoolUsers] = useState(false);

    const schoolsList = useSelector((state) => state.schools?.schools || []);
    const selectedSchool = schoolsList.find((s) => s.Id === selectedSchoolId) || null;

    const fetchSchoolUsers = useCallback((schoolId, t) => {
        setSchoolUsers([]);
        setLoadingSchoolUsers(true);
        api.post("/users/", {
            SchoolId: schoolId,
            UserTypeId: 3,
            IsDeleted: false,
            Limit: 100,
        })
            .then((response) => {
                setSchoolUsers(response.data?.data || []);
                setLoadingSchoolUsers(false);
            })
            .catch(() => {
                setLoadingSchoolUsers(false);
                toast.error(t("Error.NoConnection.Title"));
            });
    }, []);

    const handleCloseSchoolModal = useCallback(() => {
        setSchoolModalOpen(false);
        setSelectedSchoolId(null);
        setSchoolUsers([]);
        setLoadingSchoolUsers(false);
    }, []);

    // --- Modal controls ---
    const handleCloseModal = useCallback(() => {
        if (activeSubModal) {
            setActiveSubModal(null);
        } else {
            setActiveModal(null);
            setActiveUser(null);
        }
    }, [activeSubModal]);

    const handleOpenModal = useCallback((modalType, item) => {
        setActiveUser(item);
        if (activeModal === "moreInfo" && ["bills", "notes", "changeLoginInfo", "change_password", "edit", "changeSchool", "move", "errorReports", "contactMessages", "downloadFiles", "lessonMarks", "lessonViews"].includes(modalType)) {
            setActiveSubModal(modalType);
        } else {
            setActiveModal(modalType);
            setActiveSubModal(null);
        }
    }, [activeModal]);

    // --- Toast helper ---
    const toastSuccess = (id, icon, titleKey, bodyKey) => {
        toast.update(id, {
            render: () => (
                <div>
                    <div className="h5"><i className={`icon ${icon}`}></i> {t(titleKey)}</div>
                    {bodyKey && <div className="h6">{t(bodyKey)}</div>}
                </div>
            ),
            type: toast.TYPE.SUCCESS,
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const toastError = (id, icon, titleKey, bodyKey) => {
        toast.update(id, {
            render: () => (
                <div>
                    <div className="h5"><i className={`icon ${icon}`}></i> {t(titleKey)}</div>
                    {bodyKey && <div className="h6">{t(bodyKey)}</div>}
                </div>
            ),
            type: toast.TYPE.ERROR,
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const toastConnectionError = (id) => {
        toastError(id, "fal fa-wifi-slash", "Error.NoConnection.Title", "Error.NoConnection.Body");
    };

    const startProcessing = () => toast(t("Processing"), { position: "bottom-left", autoClose: false });

    // --- Confirmation Toast Builder ---
    const showConfirmToast = (message, onConfirm, onCancel) => {
        const _toastId = startProcessing();
        toast.update(_toastId, {
            render: () => (
                <div style={{ direction: "rtl", textAlign: "right" }}>
                    <i className="fa-spin fas fa-spinner"></i> {message}
                    <div className="row row-cols-2 mt-3">
                        <div className="col">
                            <button type="button" className="btn btn-danger btn-sm btn-block" onClick={() => { if (onCancel) onCancel(); toast.dismiss(_toastId); }}>
                                <i className="icon fal fa-trash-undo ml-1"></i> {t("undo")}
                            </button>
                        </div>
                        <div className="col">
                            <button type="button" className="btn btn-light btn-sm btn-block" onClick={() => onConfirm(_toastId)}>
                                <i className="icon fal fa-recycle ml-1"></i> {t("confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            ),
            type: toast.TYPE.DARK,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
        return _toastId;
    };

    const showProcessingSpinner = (toastId) => {
        toast.update(toastId, {
            render: () => <div><i className="fa-spin fas fa-spinner"></i> {t("Processing")}</div>,
            autoClose: false,
            closeOnClick: false,
        });
    };

    // =====================
    // MODAL FORM SUBMISSIONS
    // =====================

    const handleSubmitEditProfile = useCallback((postData) => {
        const _toastId = startProcessing();
        api.put(`/Users/${activeUser.Id}`, postData)
            .then((response) => {
                if (response.data.code === "success") {
                    dispatch(update(activeUser.Id, postData));
                    toastSuccess(_toastId, "fal fa-shield-check", "Pages.ProfileEdit.success_toast.Title", "Pages.ProfileEdit.success_toast.Body");
                    handleCloseModal();
                } else if (response.data.code === "exists_email") {
                    toastError(_toastId, "fal fa-bug", "Pages.Register.Email.error_toast.Title", "Pages.Register.Email.error_toast.Body");
                } else {
                    toastError(_toastId, "fal fa-bug", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body");
                }
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser, dispatch]);

    const handleSubmitChangePassword = useCallback((postData) => {
        const _toastId = startProcessing();
        api.put(`/Users/${activeUser.Id}?ChangePassword=true`, postData)
            .then((response) => {
                if (response.data.code === "success") {
                    toastSuccess(_toastId, "fal fa-shield-check", "Pages.ProfileEdit.success_toast.Title", "Pages.ProfileEdit.success_toast.Body");
                    handleCloseModal();
                } else {
                    toastError(_toastId, "fal fa-bug", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body");
                }
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser]);

    const handleSubmitLoginInfo = useCallback((postData) => {
        const _toastId = startProcessing();
        api.post(`/ChangeUserLoginInfo/${activeUser.Id}`, postData)
            .then((response) => {
                if (response.data.code === "success") {
                    toastSuccess(_toastId, "fal fa-shield-check", "Pages.ProfileEdit.success_toast.Title", "Pages.ProfileEdit.success_toast.Body");
                    dispatch(update(activeUser.Id, { ...activeUser, Email: postData.Email, Username: postData.Username }));
                    handleCloseModal();
                } else if (response.data.code === "exists_username") {
                    toastError(_toastId, "fal fa-bug", "Pages.Register.Username.error_toast.Title", "Pages.Register.Username.error_toast.Body");
                } else if (response.data.code === "exists_email") {
                    toastError(_toastId, "fal fa-bug", "Pages.Register.Email.error_toast.Title", "Pages.Register.Email.error_toast.Body");
                } else {
                    toastError(_toastId, "fal fa-bug", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title", "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body");
                }
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser, dispatch]);

    const handleSubmitChangeSchool = useCallback((schoolId, schoolName) => {
        const _toastId = startProcessing();
        if (schoolId) {
            api.post(`/ChangeUserSchool/${activeUser.Id}`, { SchoolId: schoolId })
                .then(() => {
                    toastSuccess(_toastId, "fal fa-school", "Pages.UsersControl.toast.ChangeSchool.Title", "Pages.UsersControl.toast.ChangeSchool.Body");
                    dispatch(update(activeUser.Id, { ...activeUser, School: { Id: schoolId, Name: schoolName } }));
                    handleCloseModal();
                })
                .catch(() => toastConnectionError(_toastId));
        } else {
            toastError(_toastId, "fal fa-bug", "Pages.UsersControl.toast.ChangeSchool.error", null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser, dispatch]);

    const handleSubmitChangeClassRoom = useCallback((classRoomId) => {
        const _toastId = startProcessing();
        if (classRoomId) {
            api.post(`/ChangeStudentClass/${activeUser.Id}`, { ClassRoomId: classRoomId })
                .then(() => {
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5"><i className="icon fal fa-exchange"></i>{t("Pages.UsersControl.toast.ChangeClassRoom.Title")}</div>
                                <div className="h6">{t("Pages.UsersControl.toast.ChangeClassRoom.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.SUCCESS,
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    handleCloseModal();
                })
                .catch(() => toastConnectionError(_toastId));
        } else {
            toastError(_toastId, "fal fa-bug", "Pages.UsersControl.toast.ChangeClassRoom.error", null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser]);

    // ==============================
    // ADMINISTRATIVE DIRECT ACTIONS
    // ==============================

    const handleRestoreUser = useCallback((item) => {
        const _toastId = startProcessing();
        api.post(`/Users/${item.Id}`)
            .then((response) => {
                if (response.data.code === "success") {
                    toastSuccess(_toastId, "fal fa-trash-undo-alt", "Pages.ProfileEdit.restore_toast.Title", "Pages.ProfileEdit.restore_toast.Body");
                    handleSearchSync();
                }
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSearchSync]);

    const handleFireUser = useCallback((item) => {
        const _toastId = startProcessing();
        api.delete(`/Users/${item.Id}?HardDelete=${true}`)
            .then(() => {
                toastSuccess(_toastId, "fal fa-trash-undo-alt", "Pages.ProfileEdit.delete_toast.Title", "Pages.ProfileEdit.delete_toast.Body");
                handleSearchSync();
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSearchSync]);

    const handleUseAsUser = useCallback((item) => {
        showConfirmToast(t("UserAsProcessing"), (_toastId) => {
            showProcessingSpinner(_toastId);
            api.post(`/UserAsUser/${item.Id}`)
                .then((response) => {
                    if (response.data.code === "success") {
                        let prevToken = localStorage.getItem("token");
                        localStorage.setItem("prevToken", prevToken);
                        localStorage.setItem("token", response.data.token);
                        dispatch(userLogin(response.data));
                        setToken(response.data.token);

                        toast.update(_toastId, {
                            render: () => (
                                <div>
                                    <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.Login.success_toast.Title")}</div>
                                    <div className="h6">{t("Pages.Login.success_toast.Body")}</div>
                                </div>
                            ),
                            type: toast.TYPE.INFO,
                            autoClose: 5000,
                        });
                        history.push("/Profile");
                    }
                })
                .catch(() => toastConnectionError(_toastId));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleTestUser = useCallback((item) => {
        showConfirmToast(t("TestUserProcessing"), (_toastId) => {
            showProcessingSpinner(_toastId);
            api.put(`/UserAsUser/${item.Id}`)
                .then(() => {
                    dispatch(update(item.Id, { ...item, IsForTest: true }));
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.UsersControl.toast.test_user.Title")}</div>
                                <div className="h6">{t("Pages.UsersControl.toast.test_user.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.INFO,
                        autoClose: 5000,
                    });
                })
                .catch(() => toastConnectionError(_toastId));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleChangePermission = useCallback((item) => {
        showConfirmToast(t("ChangePermissionUserProcessing"), (_toastId) => {
            showProcessingSpinner(_toastId);
            const postData = { UserTypeId: item.UserType.Id === 3 ? 4 : 3 };
            api.post(`/ChangeUserType/${item.Id}`, postData)
                .then(() => {
                    handleSearchSync();
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.UsersControl.toast.ChangePermission.Title")}</div>
                                <div className="h6">{t("Pages.UsersControl.toast.ChangePermission.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.INFO,
                        autoClose: 5000,
                    });
                })
                .catch(() => toastConnectionError(_toastId));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSearchSync]);

    const handleChangeUserType = useCallback((item) => {
        showConfirmToast(t("ChangePermissionUserProcessing"), (_toastId) => {
            toast.dismiss(_toastId);
            if (item.UserType.Id === 4) {
                // Convert type 4 back to type 2 (no school needed)
                const processingId = startProcessing();
                api.put(`/ChangeUserType/${item.Id}`, { SchoolId: 0, UserTypeId: 2 })
                    .then(() => {
                        handleSearchSync();
                        toast.update(processingId, {
                            render: () => (
                                <div>
                                    <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.UsersControl.toast.ChangePermission.Title")}</div>
                                    <div className="h6">{t("Pages.UsersControl.toast.ChangePermission.Body")}</div>
                                </div>
                            ),
                            type: toast.TYPE.INFO,
                            autoClose: 5000,
                        });
                    })
                    .catch(() => toastConnectionError(processingId));
            } else {
                // Type 2 → needs school selection modal
                handleOpenModal("changeSchool", item);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSearchSync, handleOpenModal]);

    const handleSubmitChangeUserTypeWithSchool = useCallback((schoolId, schoolName) => {
        if (!schoolId) {
            toast.error(t("Pages.UsersControl.toast.ChangeSchool.error"));
            return;
        }
        const _toastId = startProcessing();
        const postData = {
            SchoolId: schoolId,
            UserTypeId: activeUser.UserType.Id === 2 ? 4 : 2,
        };
        api.put(`/ChangeUserType/${activeUser.Id}`, postData)
            .then(() => {
                handleSearchSync();
                toast.update(_toastId, {
                    render: () => (
                        <div>
                            <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.UsersControl.toast.ChangePermission.Title")}</div>
                            <div className="h6">{t("Pages.UsersControl.toast.ChangePermission.Body")}</div>
                        </div>
                    ),
                    type: toast.TYPE.INFO,
                    autoClose: 5000,
                });
                handleCloseModal();
            })
            .catch(() => toastConnectionError(_toastId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUser, handleSearchSync, handleCloseModal]);

    const handleChangeAccessToAllClass = useCallback((item) => {
        showConfirmToast(t("ChangeAccessToClassProcessing"), (_toastId) => {
            showProcessingSpinner(_toastId);
            api.post(`/ChangeAccessToAllClass/${item.Id}`)
                .then(() => {
                    handleSearchSync();
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5"><i className="icon fal fa-shield-check"></i> {t("Pages.UsersControl.toast.ChangePermission.Title")}</div>
                                <div className="h6">{t("Pages.UsersControl.toast.ChangePermission.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.INFO,
                        autoClose: 5000,
                    });
                })
                .catch(() => toastConnectionError(_toastId));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleSearchSync]);

    const handleRemoveUserWithUndo = useCallback((item) => {
        dispatch(remove(item.Id));
        const _toastId = startProcessing();
        toast.update(_toastId, {
            render: () => (
                <div style={{ direction: "rtl", textAlign: "right" }}>
                    <i className="fa-spin fas fa-spinner"></i> {t("DeleteProcessing")}
                    <div className="row row-cols-2 mt-3">
                        <div className="col">
                            <button type="button" className="btn btn-danger btn-sm btn-block" onClick={() => { dispatch(undo(item.Id)); toast.dismiss(_toastId); }}>
                                <i className="icon fal fa-trash-undo ml-1"></i> {t("undo")}
                            </button>
                        </div>
                        <div className="col">
                            <button type="button" className="btn btn-light btn-sm btn-block" onClick={() => {
                                showProcessingSpinner(_toastId);
                                api.delete(`/Users/${item.Id}`)
                                    .then(() => {
                                        dispatch(clear(item.Id));
                                        toastSuccess(_toastId, "fal fa-exclamation-triangle", "Pages.UsersControl.toast.Delete.Title", "Pages.UsersControl.toast.Delete.Body");
                                    })
                                    .catch(() => toastConnectionError(_toastId));
                            }}>
                                <i className="icon fal fa-recycle ml-1"></i> {t("confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            ),
            type: toast.TYPE.DARK,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // =====================
    // ACTION DISPATCHER
    // =====================

    const handleActionTrigger = useCallback((actionType, item) => {
        switch (actionType) {
            case "edit":
            case "bills":
            case "notes":
            case "change_password":
            case "changeLoginInfo":
            case "move":
            case "moreInfo":
            case "errorReports":
            case "contactMessages":
            case "downloadFiles":
            case "lessonMarks":
            case "lessonViews": {
                handleOpenModal(actionType, item);
                break;
            }
            case "showChangeSchool": {
                handleOpenModal("changeSchool", item);
                break;
            }
            case "restore":
                handleRestoreUser(item);
                break;
            case "fire":
                handleFireUser(item);
                break;
            case "useAs":
                handleUseAsUser(item);
                break;
            case "testUser":
                handleTestUser(item);
                break;
            case "changePermission":
                handleChangePermission(item);
                break;
            case "changeUserType":
                handleChangeUserType(item);
                break;
            case "accessToAllClass":
                handleChangeAccessToAllClass(item);
                break;
            case "remove":
                handleRemoveUserWithUndo(item);
                break;
            case "schoolInfo": {
                if (item && item.Id) {
                    setSelectedSchoolId(item.Id);
                    setSchoolModalOpen(true);
                    dispatch(fetchSchoolDetails(item.Id));
                    fetchSchoolUsers(item.Id, t);
                }
                break;
            }
            default:
                break;
        }
    }, [
        dispatch, fetchSchoolUsers, t,
        handleOpenModal, handleRestoreUser, handleFireUser, handleUseAsUser,
        handleTestUser, handleChangePermission, handleChangeUserType,
        handleChangeAccessToAllClass, handleRemoveUserWithUndo,
    ]);

    return {
        // Modal state
        activeModal,
        activeSubModal,
        activeUser,
        handleCloseModal,
        handleOpenModal,

        // School details modal states
        schoolModalOpen,
        selectedSchool,
        schoolUsers,
        loadingSchoolUsers,
        handleCloseSchoolModal,

        // Form submission handlers
        handleSubmitEditProfile,
        handleSubmitChangePassword,
        handleSubmitLoginInfo,
        handleSubmitChangeSchool,
        handleSubmitChangeClassRoom,
        handleSubmitChangeUserTypeWithSchool,

        // Action dispatcher
        handleActionTrigger,
    };
};

export default useUserActions;
